import { useAuth0 } from "@auth0/auth0-react";
import { useEffect } from "react";
import { getCookie, removeCookie } from "utils/v3/CookieUtil";
import {
  ACCESS_TOKEN_COOKIE,
  getAccessTokenSecondsRemaining,
  refreshAccessTokenCookie,
  REFRESH_WHEN_SECONDS_REMAINING
} from "utils/v3/accessTokenRefresh";

const CHECK_INTERVAL_IN_MILLISECONDS = 5000;

/** Cookies from the previous schedule-based implementation; clear them so they do not linger. */
const LEGACY_SCHEDULE_COOKIES = ["refreshTimer", "refreshTimerId"];

/**
 * Keeps the `accessToken` cookie holding a JWT that is still valid.
 *
 * Every few seconds this reads the JWT's own `exp` and, once it is inside the
 * refresh window, asks Auth0 for a new token and rewrites the cookie. Driving
 * the refresh off `exp` (rather than a fixed cadence) means the cookie can
 * never hold an expired token while the member is still on the page, which
 * is what `AppRouter` checks on every route change.
 *
 * If the cookie is gone (logout, MFA re-auth), nothing is done; the router
 * owns that path.
 */
const RefreshTimer = () => {
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    LEGACY_SCHEDULE_COOKIES.forEach(removeCookie);

    const tick = () => {
      const secondsRemaining = getAccessTokenSecondsRemaining(
        getCookie(ACCESS_TOKEN_COOKIE)
      );
      if (secondsRemaining === null) {
        return;
      }
      if (secondsRemaining > REFRESH_WHEN_SECONDS_REMAINING) {
        return;
      }
      refreshAccessTokenCookie(getAccessTokenSilently);
    };

    const intervalId = setInterval(tick, CHECK_INTERVAL_IN_MILLISECONDS);
    return () => {
      clearInterval(intervalId);
    };
  }, [getAccessTokenSilently]);

  return null;
};

export default RefreshTimer;
